// DNA CONNECT — Meus Convites (Festa Junina / Eventos V2) — Visual Premium
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { FamilyPurchase } from '@/components/eventos/FamilyPurchase';
import { EventAuthCard } from '@/components/eventos/EventAuthCard';
import { Loader2, Download, Share2, Maximize2, Printer, AlertTriangle, CheckCircle2, XCircle, ChevronRight, Ticket, MapPin, Calendar, Clock, Gift, Tag, Sparkles } from 'lucide-react';

const API_URL = window.location.origin;
const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('familyToken') || ''}` });

const EVENTO_DEFAULTS = {
  data: '20/06/2026', horario_inicio: '10:00', horario_fim: '17:00',
  local: 'NOVO DNA - Estrada Benedito Cesário de Oliveira, 156 - Vila Iasi',
  valor: 'R$ 12,00', sorteio: 'Bicicleta',
  frase: 'Vamos nos divertir de forma intencional com união e alegria',
  tema: 'Sociedade transformada, gestão eficiente',
};

// ─── Decoração: bandeirinhas + bicicleta ──────────────────────────
function Bandeirinhas({ className = '' }) {
  const cores = ['#7B3F98', '#16a34a', '#eab308', '#dc2626', '#0ea5e9'];
  return (
    <svg className={className} viewBox="0 0 600 40" preserveAspectRatio="none" aria-hidden>
      <path d="M0 4 Q300 28 600 4" stroke="#475569" strokeWidth="1.2" fill="none" />
      {Array.from({ length: 20 }).map((_, i) => (
        <g key={i} transform={`translate(${i * 30 + 6}, 8)`}>
          <polygon points="0,0 18,0 9,18" fill={cores[i % cores.length]} />
        </g>
      ))}
    </svg>
  );
}
function BicicletaIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="6" cy="17" r="3" /><circle cx="18" cy="17" r="3" />
      <path d="M6 17 L10 7 L14 17 L18 17 M10 7 L14 7" />
    </svg>
  );
}

// Pequenos enfeites SVG (papel premium)
function FogueiraIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3c-1.5 2.5 1.2 3.5 0 6-1.2 2.5-3 3-3 5.5a3 3 0 006 0c0-2-1.5-2.5-1.5-4.5 1.5 1 2.5 2 2.5 4a4 4 0 11-8 0c0-3.5 4-4 4-11z" />
      <path d="M4 21h16M7 19l-2 2M17 19l2 2" />
    </svg>
  );
}
function ChapeuPalhaIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 17c3-1 6-1.5 9-1.5s6 .5 9 1.5" />
      <path d="M8 15c0-4 1.5-8 4-8s4 4 4 8" />
      <path d="M6 17c0 1.5 2.7 2.5 6 2.5s6-1 6-2.5" />
      <path d="M9 11h6" />
    </svg>
  );
}
function PipaIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3l6 6-6 6-6-6 6-6z" /><path d="M12 3v12M6 9h12" />
      <path d="M12 15l-1 3 1-1 1 1-1-3" /><path d="M12 18c-1 2-2 3-3 4M12 18c1 2 2 3 3 4" />
    </svg>
  );
}
function PapelTextura({ className = '' }) {
  return (
    <svg className={className} aria-hidden viewBox="0 0 200 200" preserveAspectRatio="none">
      <defs>
        <pattern id="dots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1.2" cy="1.2" r="0.7" fill="currentColor" />
        </pattern>
      </defs>
      <rect width="200" height="200" fill="url(#dots)" />
    </svg>
  );
}

// ─── Tarja de status ──────────────────────────────────────────────
function StatusBanner({ estado }) {
  const map = {
    cancelado: { txt: 'CANCELADO', bg: 'bg-rose-600' },
    esgotado:  { txt: 'UTILIZADO', bg: 'bg-slate-700' },
    pendente:  { txt: 'PENDENTE',  bg: 'bg-amber-600' },
    parcial:   { txt: 'EM USO',    bg: 'bg-amber-500' },
  };
  const cfg = map[estado];
  if (!cfg) return null;
  return (
    <div className={`absolute top-6 right-[-40px] rotate-[18deg] ${cfg.bg} text-white font-black text-xs px-12 py-1.5 shadow-lg tracking-widest pointer-events-none select-none`}>
      {cfg.txt}
    </div>
  );
}

function EstadoBadge({ estado }) {
  if (estado === 'cancelado') return <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 px-2 py-0.5 rounded text-xs border border-rose-200"><XCircle className="w-3 h-3" />Cancelado</span>;
  if (estado === 'esgotado')  return <span className="inline-flex items-center gap-1 text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-xs border"><AlertTriangle className="w-3 h-3" />Utilizado</span>;
  if (estado === 'pendente')  return <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-xs border border-amber-200"><AlertTriangle className="w-3 h-3" />Pendente</span>;
  if (estado === 'parcial')   return <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded text-xs border border-amber-200">Em uso</span>;
  return <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded text-xs border border-emerald-200"><CheckCircle2 className="w-3 h-3" />Válido</span>;
}

// ═══════════════════════════════════════════════════════════════════
// PÁGINA: MEUS CONVITES
// ═══════════════════════════════════════════════════════════════════
export default function DnaConnectConvites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem('familyToken') || '';
        console.log('[DNA_CONNECT_CONVITES] tokenLen=', t.length);
        const r = await fetch(`${API_URL}/api/festa-junina/familia/convites`, { headers: headers() });
        if (!r.ok) { console.warn('[DNA_CONNECT_CONVITES] HTTP', r.status); throw new Error('falha'); }
        const j = await r.json();
        console.log('[DNA_CONNECT_CONVITES] convites encontrados=', (j.items || []).length, j.items);
        setItems(j.items || []);
      } catch (e) { console.error('[DNA_CONNECT_CONVITES] erro', e); toast.error('Não foi possível carregar seus convites.'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="max-w-3xl mx-auto p-3 md:p-5">
      <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-purple-700 to-emerald-700 text-white mb-4">
        <Bandeirinhas className="w-full h-6" />
        <div className="px-5 py-4">
          <div className="text-[10px] uppercase tracking-widest text-purple-100">DNA Connect</div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Ticket className="w-6 h-6" /> Meus Convites</h1>
          <p className="text-purple-100 italic text-sm mt-1">Apresente seu QR Code na entrada — basta abrir, mostrar a tela ou salvar a imagem.</p>
        </div>
        <Bandeirinhas className="w-full h-6 rotate-180" />
      </div>

      <div className="mb-3">
        <Link to="/familia/eventos" className="text-xs text-purple-700 underline">Ver todos os eventos →</Link>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Carregando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border bg-white p-8 text-center text-slate-500">
          Nenhum convite disponível ainda.
          <div className="text-xs mt-1">Assim que a escola publicar seu convite ele aparecerá aqui.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="dna-convites-list">
          {items.map((c) => (
            <button key={c.id} onClick={() => setSelected(c)}
              className="w-full text-left bg-white border rounded-2xl p-4 hover:shadow-md transition flex items-center gap-3"
              data-testid={`dna-convite-${c.id}`}>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-2">
                <QRCodeCanvas value={`${API_URL}/v2/festa-junina/checkin/${c.token}`} size={64} level="M" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wide text-purple-700 font-semibold">{c.evento?.nome || 'Evento'}</div>
                <div className="text-lg font-extrabold text-slate-900 truncate">{c.aluno_nome}</div>
                <div className="text-xs text-slate-600 truncate">{c.turma_name} · {c.quantidade_total} acessos · {c.restantes} restante(s)</div>
                <div className="text-[11px] mt-1 flex flex-wrap gap-1 items-center">
                  <span className="font-mono text-purple-700">#{c.numero_str}</span>
                  <EstadoBadge estado={c.estado} />
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          ))}
        </div>
      )}

      {selected && <ConviteDialog convite={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONVITE DIGITAL (Premium)
// ═══════════════════════════════════════════════════════════════════
function ConviteDialog({ convite, onClose }) {
  const qrRef = useRef(null);
  const url = `${API_URL}/v2/festa-junina/checkin/${convite.token}`;
  const [full, setFull] = useState(false);
  const ev = { ...EVENTO_DEFAULTS, ...(convite.evento || {}) };

  // Salvar PNG: monta um canvas com layout
  const downloadPng = () => {
    try {
      const canvas = qrRef.current?.querySelector('canvas');
      if (!canvas) return;
      const w = 720, h = 1100;
      const out = document.createElement('canvas');
      out.width = w; out.height = h;
      const ctx = out.getContext('2d');
      // BG
      ctx.fillStyle = '#f1f5f9'; ctx.fillRect(0, 0, w, h);
      // Header
      const grad = ctx.createLinearGradient(0, 0, 0, 180);
      grad.addColorStop(0, '#64327D'); grad.addColorStop(1, '#059669');
      ctx.fillStyle = grad; ctx.fillRect(40, 30, w - 80, 180);
      // Logo D
      ctx.fillStyle = '#ffffff'; ctx.fillRect(60, 56, 50, 50);
      ctx.fillStyle = '#64327D'; ctx.font = 'bold 32px sans-serif';
      ctx.fillText('D', 75, 92);
      // Headertxt
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px sans-serif';
      ctx.fillText('NOVO DNA · DNA CONNECT', 130, 78);
      ctx.font = 'bold 36px sans-serif';
      ctx.fillText(ev.nome || 'Festa Junina 2026', 60, 150);
      ctx.font = 'italic 14px sans-serif';
      ctx.fillText('"' + (ev.frase || '') + '"', 60, 180);

      // Card branco
      ctx.fillStyle = '#ffffff'; ctx.fillRect(40, 240, w - 80, h - 280);

      // Número gigante
      ctx.fillStyle = '#6b7280'; ctx.font = '12px sans-serif';
      ctx.fillText('CONVITE', 70, 290);
      ctx.fillStyle = '#7B3F98'; ctx.font = 'bold 96px monospace';
      ctx.fillText('#' + convite.numero_str, 60, 380);

      // QR centralizado
      ctx.drawImage(canvas, (w - 320) / 2, 410, 320, 320);
      ctx.fillStyle = '#7B3F98'; ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Apresente este QR Code na entrada', w / 2, 760);
      ctx.textAlign = 'left';

      // Info
      ctx.fillStyle = '#0f172a'; ctx.font = 'bold 22px sans-serif';
      ctx.fillText(convite.aluno_nome || '', 70, 810);
      ctx.fillStyle = '#475569'; ctx.font = '14px sans-serif';
      ctx.fillText(`${convite.turma_name || '—'} · ${convite.quantidade_total} acessos · ${convite.restantes} restante(s)`, 70, 836);
      ctx.fillText(`Data: ${ev.data || ''} · ${ev.horario_inicio || ''}–${ev.horario_fim || ''}`, 70, 870);
      ctx.fillText(`Local: ${ev.local || ''}`, 70, 896);
      ctx.fillText(`Valor pagante: ${ev.valor || 'R$ 12,00'} · Sorteio: ${ev.premio || ev.sorteio || 'Bicicleta'}`, 70, 922);
      ctx.fillText(`Tema: ${ev.tema || ''}`, 70, 948);

      const link = document.createElement('a');
      link.download = `convite_${convite.numero_str}.png`;
      link.href = out.toDataURL('image/png');
      link.click();
    } catch (e) { toast.error('Não foi possível gerar a imagem.'); }
  };

  const compartilhar = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Convite ${convite.aluno_nome}`, text: `Convite #${convite.numero_str} — ${ev.nome}`, url });
      } else {
        await navigator.clipboard.writeText(url); toast.success('Link copiado.');
      }
    } catch { /* user cancel */ }
  };

  const imprimir = () => window.print();

  return (
    <div className={`fixed inset-0 z-50 ${full ? 'bg-white' : 'bg-black/70 backdrop-blur-sm'} flex items-center justify-center p-2 md:p-3 print:p-0 print:bg-white animate-[fadeIn_.25s_ease-out]`} data-testid="dna-convite-dialog">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        @keyframes shine { 0%,100% { box-shadow: 0 0 0 0 rgba(124,58,237,.18) } 50% { box-shadow: 0 0 0 14px rgba(124,58,237,0) } }
        .qr-glow { animation: shine 2.6s ease-in-out infinite; }
        @media print {
          @page { size: A4; margin: 10mm; }
          body * { visibility: hidden; }
          .convite-print, .convite-print * { visibility: visible; }
          .convite-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; }
          .no-print { display: none !important; }
          .qr-glow { animation: none !important; box-shadow: none !important; }
        }
      `}</style>

      <div className={`relative w-full max-w-sm ${full ? 'max-h-screen h-full' : ''} convite-print`}>
        {/* Borda externa: papel premium creme com pontilhado */}
        <div className={`relative rounded-[22px] shadow-2xl overflow-hidden ${full ? 'rounded-none h-full overflow-y-auto' : ''}`}
             style={{ background: 'linear-gradient(180deg,#fdf6ec 0%,#fbf2e1 100%)' }}>

          {/* Textura sutil de pontos */}
          <div className="pointer-events-none absolute inset-0 text-amber-900/10 opacity-40"><PapelTextura className="w-full h-full" /></div>

          {/* Tarja diagonal de status */}
          <StatusBanner estado={convite.estado} />

          {/* HEADER ─ compacto */}
          <div className="relative bg-gradient-to-br from-purple-800 via-purple-700 to-emerald-700 text-white px-4 pt-2 pb-3 overflow-hidden">
            <Bandeirinhas className="absolute -top-0.5 left-0 w-full h-4 opacity-90" />
            {/* enfeites pequenos */}
            <ChapeuPalhaIcon className="absolute top-4 right-2 w-6 h-6 text-amber-200/60" />
            <FogueiraIcon className="absolute bottom-1.5 left-2 w-4 h-4 text-amber-300/70" />

            <div className="relative flex items-start gap-2 mt-4">
              <div className="bg-white text-purple-700 w-9 h-9 rounded-lg flex items-center justify-center font-black text-base shadow-md shrink-0">D</div>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-[0.16em] text-purple-100/90 leading-none">Novo DNA · DNA Connect</div>
                <div className="text-[17px] font-black leading-tight tracking-tight mt-0.5">{ev.nome || 'Festa Junina 2026'}</div>
                <div className="text-[10px] text-amber-100/90 italic mt-0.5 leading-snug line-clamp-2">"{ev.frase}"</div>
              </div>
            </div>

            {/* Tema badge */}
            <div className="mt-1.5 inline-flex items-center gap-1 bg-white/10 border border-white/20 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider text-amber-100">
              <Tag className="w-2.5 h-2.5" />
              <span className="font-semibold">Tema:</span>
              <span className="opacity-95 truncate max-w-[200px]">{ev.tema || EVENTO_DEFAULTS.tema}</span>
            </div>
          </div>

          {/* Recorte côncavo (orelhinhas) */}
          <div className="relative h-3">
            <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full" style={{ background: 'rgba(0,0,0,.85)' }} />
            <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full" style={{ background: 'rgba(0,0,0,.85)' }} />
            <div className="absolute inset-x-5 top-1 border-t-2 border-dashed border-amber-700/30" />
          </div>

          {/* CORPO ─ número, aluno, status */}
          <div className="relative px-4 pt-1 pb-1.5 text-center">
            <div className="text-[9px] uppercase tracking-[0.2em] text-purple-700/70 font-semibold">Convite Digital</div>
            <div className="text-[36px] md:text-[40px] font-mono font-black bg-gradient-to-br from-purple-800 to-emerald-700 bg-clip-text text-transparent leading-none tracking-tight mt-0.5">
              #{convite.numero_str}
            </div>
            <div className="text-base font-extrabold uppercase text-slate-900 mt-1 truncate tracking-tight">{convite.aluno_nome}</div>
            <div className="text-[11px] text-slate-600">
              {convite.turma_name || '—'} · <strong>{convite.quantidade_total}</strong> acessos · <span className="text-emerald-700 font-semibold">{convite.restantes} restante(s)</span>
            </div>
            <div className="mt-1"><EstadoBadge estado={convite.estado} /></div>
          </div>

          {/* QR (destaque principal) */}
          <div ref={qrRef} className="relative flex justify-center px-4 pb-1">
            <div className={`bg-white rounded-xl p-2.5 border border-purple-200/80 ${convite.estado === 'cancelado' || convite.estado === 'esgotado' ? '' : 'qr-glow'}`}>
              <QRCodeCanvas value={url} size={196} level="H" includeMargin />
            </div>
          </div>
          <div className="text-center text-[11px] text-purple-800 font-semibold pt-1 pb-2 flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3 text-amber-500" />
            Apresente este QR Code na entrada
            <Sparkles className="w-3 h-3 text-amber-500" />
          </div>

          {/* Linha compacta de infos: Data · Horário · Valor · Sorteio */}
          <div className="px-4 pb-2 grid grid-cols-2 gap-1.5">
            <InfoCard icon={<Calendar className="w-3 h-3" />} label="Data" value={ev.data || '—'} />
            <InfoCard icon={<Clock className="w-3 h-3" />} label="Horário" value={`${ev.horario_inicio || ''}${ev.horario_fim ? '–' + ev.horario_fim : ''}`} />
            <InfoCard icon={<MapPin className="w-3 h-3" />} label="Local" value={ev.local || '—'} className="col-span-2" />
            <InfoCard icon={<Tag className="w-3 h-3" />} label="Valor" value={ev.valor || 'R$ 12,00'} />
            <InfoCard icon={<Gift className="w-3 h-3" />} label="Sorteio" value={ev.premio || ev.sorteio || 'Bicicleta'} accent />
          </div>

          {/* Divisor de corte */}
          <div className="relative h-3">
            <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full" style={{ background: 'rgba(0,0,0,.85)' }} />
            <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full" style={{ background: 'rgba(0,0,0,.85)' }} />
            <div className="absolute inset-x-5 top-1 border-t-2 border-dashed border-amber-700/30" />
          </div>

          {/* Via escola (canhoto) compacto */}
          <div className="px-4 py-2 flex justify-between items-center text-[10px] text-slate-700 bg-white/40">
            <div>
              <div className="text-[8px] uppercase tracking-widest text-slate-500 leading-none">Via Escola</div>
              <div className="font-mono text-purple-800 font-bold text-[13px] leading-tight">#{convite.numero_str}</div>
            </div>
            <div className="text-right min-w-0">
              <div className="font-semibold uppercase text-[10px] truncate max-w-[180px]">{convite.aluno_nome}</div>
              <div className="text-slate-500 text-[9px] leading-tight">{convite.turma_name || '—'}</div>
            </div>
          </div>

          {/* AÇÕES ─ botões compactos */}
          <div className="px-3 py-2 border-t border-amber-900/10 grid grid-cols-2 gap-1.5 no-print bg-white/70 backdrop-blur-sm">
            <Button variant="outline" size="sm" onClick={() => setFull((f) => !f)} className="gap-1 rounded-full border-purple-200 hover:bg-purple-50 h-8 text-[11px]">
              <Maximize2 className="w-3 h-3" />Tela cheia
            </Button>
            <Button variant="outline" size="sm" onClick={imprimir} className="gap-1 rounded-full border-purple-200 hover:bg-purple-50 h-8 text-[11px]">
              <Printer className="w-3 h-3" />Imprimir
            </Button>
            <Button variant="outline" size="sm" onClick={downloadPng} className="gap-1 rounded-full border-purple-200 hover:bg-purple-50 h-8 text-[11px]" data-testid="dna-convite-download">
              <Download className="w-3 h-3" />Salvar PNG
            </Button>
            <Button size="sm" onClick={compartilhar} className="gap-1 rounded-full text-white shadow-md hover:shadow-lg transition bg-gradient-to-r from-purple-700 to-emerald-600 hover:from-purple-800 hover:to-emerald-700 h-8 text-[11px]" data-testid="dna-convite-share">
              <Share2 className="w-3 h-3" />Compartilhar
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="col-span-2 rounded-full text-slate-500 hover:text-slate-700 h-7 text-[11px]">Fechar</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value, accent = false, className = '' }) {
  return (
    <div className={`rounded-lg border ${accent ? 'border-amber-300 bg-amber-50/70' : 'border-purple-100 bg-white/70'} backdrop-blur-sm px-2 py-1 ${className}`}>
      <div className={`flex items-center gap-1 text-[9px] uppercase tracking-wider ${accent ? 'text-amber-700' : 'text-purple-700/80'} font-semibold leading-none`}>
        {icon}<span>{label}</span>
      </div>
      <div className={`text-[11px] font-bold ${accent ? 'text-amber-900' : 'text-slate-900'} leading-tight mt-0.5 break-words`}>{value || '—'}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PÁGINA: EVENTOS DA ESCOLA (DNA Connect) — com abas Eventos + Meus Convites
// ═══════════════════════════════════════════════════════════════════
function FamilyAuthorizations() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = async () => {
    try { const r = await fetch(`${API_URL}/api/dnaconnect/eventos/autorizacoes`, { headers: headers() }); if (r.ok) setItems((await r.json()).items || []); } catch (e) { /* sem autorizações */ }
  };
  useEffect(() => { load(); }, []);
  const answer = async (c, resposta) => {
    setBusy(`${c.event_id}-${c.student_id}`);
    try {
      const r = await fetch(`${API_URL}/api/dnaconnect/eventos/${c.event_id}/autorizacoes/${c.student_id}`, { method: 'POST', headers: { ...headers(), 'Content-Type': 'application/json' }, body: JSON.stringify({ resposta }) });
      const j = await r.json(); if (!r.ok) throw new Error(j.detail);
      setItems((xs) => xs.map((x) => (x.event_id === c.event_id && x.student_id === c.student_id ? { ...x, autorizacao: j.autorizacao, resposta: j } : x)));
      toast.success(resposta === 'sim' ? 'Participação confirmada!' : 'Resposta registrada.');
    } catch (e) { toast.error(e.message || 'Falha ao responder.'); } finally { setBusy(null); }
  };
  if (!items.length) return null;
  return (
    <div className="mb-4 space-y-3" data-testid="dna-autorizacoes">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-700">Autorizações de eventos</p>
      {items.map((c) => <React.Fragment key={`${c.event_id}-${c.student_id}`}><EventAuthCard c={c} busy={busy === `${c.event_id}-${c.student_id}`} onAnswer={(r) => answer(c, r)} />{c.autorizacao !== 'negado' && <FamilyPurchase eventId={c.event_id} studentId={c.student_id} />}</React.Fragment>)}
    </div>
  );
}

export function DnaConnectEventos() {
  const [tab, setTab] = useState('eventos');
  const [events, setEvents] = useState([]);
  const [convites, setConvites] = useState([]);
  const [loadingEv, setLoadingEv] = useState(true);
  const [loadingCv, setLoadingCv] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoadingEv(true); setLoadingCv(true);
    const tk = localStorage.getItem('familyToken') || '';
    console.log('[DNA_CONNECT_EVENTOS] tokenLen=', tk.length);
    // Eventos
    try {
      const r = await fetch(`${API_URL}/api/festa-junina/familia/eventos`, { headers: headers() });
      console.log('[DNA_CONNECT_EVENTOS] HTTP', r.status);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      console.log('[DNA_CONNECT_EVENTOS] total encontrado=', (j.items || []).length);
      setEvents(j.items || []);
    } catch (e) { console.error('[DNA_CONNECT_EVENTOS] erro ao carregar eventos', e); }
    finally { setLoadingEv(false); }
    // Convites (não bloqueia se eventos falhar)
    try {
      const r = await fetch(`${API_URL}/api/festa-junina/familia/eventos/convites`, { headers: headers() });
      console.log('[DNA_CONNECT_CONVITES] HTTP', r.status);
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const j = await r.json();
      console.log('[DNA_CONNECT_CONVITES] total encontrado=', (j.items || []).length, j.items);
      setConvites(j.items || []);
    } catch (e) { console.error('[DNA_CONNECT_CONVITES] erro ao carregar convites', e); }
    finally { setLoadingCv(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-3xl mx-auto p-3 md:p-5">
      <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-purple-700 to-emerald-700 text-white mb-4">
        <Bandeirinhas className="w-full h-6" />
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-100">DNA Connect</div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Ticket className="w-6 h-6" /> Eventos</h1>
            <p className="text-purple-100 italic text-sm mt-1">Eventos da escola e seus convites digitais.</p>
          </div>
          <Button variant="outline" onClick={load} className="bg-white/10 border-white/40 text-white hover:bg-white/20">Atualizar</Button>
        </div>
        <Bandeirinhas className="w-full h-6 rotate-180" />
      </div>

      {/* Abas */}
      <div className="flex gap-2 mb-3" data-testid="dna-eventos-tabs">
        <button
          onClick={() => setTab('eventos')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === 'eventos' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-700'}`}
          data-testid="tab-eventos">
          Eventos disponíveis {events.length > 0 && <span className="ml-1 text-[10px] opacity-80">({events.length})</span>}
        </button>
        <button
          onClick={() => setTab('convites')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold border ${tab === 'convites' ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-slate-700'}`}
          data-testid="tab-convites">
          Meus Convites {convites.length > 0 && <span className="ml-1 text-[10px] opacity-80">({convites.length})</span>}
        </button>
      </div>

      {tab === 'eventos' && <FamilyAuthorizations />}
      {tab === 'eventos' && (
        loadingEv ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Carregando eventos…</div>
        ) : events.length === 0 ? (
          <div className="rounded-xl border bg-white p-8 text-center text-slate-500">Nenhum evento publicado no momento.</div>
        ) : (
          <div className="space-y-3">
            {events.map((ev) => (
              <div key={ev.id} className="bg-white border rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-purple-700 to-emerald-700 text-white px-4 py-2">
                  <div className="text-[10px] uppercase tracking-widest text-purple-100">{ev.tema || 'Evento'}</div>
                  <div className="text-lg font-bold">{ev.nome}</div>
                </div>
                <div className="p-4 space-y-1 text-sm">
                  {ev.data && <div><strong>Data:</strong> {ev.data} {ev.horario_inicio ? `· ${ev.horario_inicio}` : ''}{ev.horario_fim ? `–${ev.horario_fim}` : ''}</div>}
                  {ev.local && <div><strong>Local:</strong> {ev.local}</div>}
                  {typeof ev.valor_ingresso === 'number' && <div><strong>Valor pagante:</strong> R$ {Number(ev.valor_ingresso).toFixed(2)}</div>}
                  {ev.premio && <div className="flex items-center gap-1 text-amber-700"><BicicletaIcon className="w-4 h-4" /><span>Sorteio: <strong>{ev.premio}</strong></span></div>}
                  {ev.frase && <div className="italic text-slate-600 text-xs">"{ev.frase}"</div>}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'convites' && (
        loadingCv ? (
          <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Carregando convites…</div>
        ) : convites.length === 0 ? (
          <div className="rounded-xl border bg-white p-8 text-center text-slate-500">Não encontramos convites publicados para seus alunos ainda.</div>
        ) : (
          <div className="space-y-3" data-testid="dna-convites-list">
            {convites.map((c) => (
              <button key={c.id} onClick={() => setSelected(c)}
                className="w-full text-left bg-white border rounded-2xl p-4 hover:shadow-md transition flex items-center gap-3"
                data-testid={`dna-convite-${c.id}`}>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-2">
                  <QRCodeCanvas value={`${API_URL}/v2/festa-junina/checkin/${c.token}`} size={64} level="M" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] uppercase tracking-wide text-purple-700 font-semibold">{c.evento?.nome || 'Evento'}</div>
                  <div className="text-lg font-extrabold text-slate-900 truncate">{c.aluno_nome}</div>
                  <div className="text-xs text-slate-600 truncate">{c.turma_name} · {c.quantidade_total} acessos · {c.restantes} restante(s)</div>
                  <div className="text-[11px] mt-1 flex flex-wrap gap-1 items-center">
                    <span className="font-mono text-purple-700">#{c.numero_str}</span>
                    <EstadoBadge estado={c.estado} />
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            ))}
          </div>
        )
      )}

      {selected && <ConviteDialog convite={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
